package co.maytes.standupbot.dashboard.components.layout

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.A
import co.maytes.standupbot.dashboard.components.marketing.PageIntro
import co.maytes.standupbot.dashboard.components.motion.Reveal
import co.maytes.standupbot.dashboard.components.widgets.ThemeToggle
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.ZIndex
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
fun MarketingShell(content: @Composable () -> Unit) {
    Column(modifier = Modifier.classNames("mk-site").fillMaxWidth()) {
        A(href = "#main", attrs = { classes("mk-skip") }) { Text("Skip to content") }
        MarketingTopNav()
        Div(attrs = { attr("id", "main"); classes("mk-main") }) { content() }
        MarketingFooter()
    }
}

@Composable
fun MarketingTopNav() {
    Box(
        modifier = Modifier
            .classNames("mk-navwrap")
            .fillMaxWidth()
            .styleModifier {
                property("position", "sticky")
                property("top", "0")
                property("z-index", "${ZIndex.nav}")
            },
    ) {
        Box(modifier = Modifier.classNames("sb-nav", "mk-pill")) {
            MarketingNavRow()
        }
    }
}

@Composable
private fun MarketingNavRow() {
    Row(
        modifier = Modifier
            .classNames("mk-wrap")
            .styleModifier {
                property("padding-top", Spacing.sm.toString())
                property("padding-bottom", Spacing.sm.toString())
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Link(
            path = "/",
            modifier = Modifier
                .classNames("sb-nav-brand")
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "10px")
                    property("text-decoration", "none")
                },
        ) {
            Image(
                src = "/logo.svg",
                modifier = Modifier.styleModifier {
                    property("width", "28px")
                    property("height", "28px")
                    property("display", "block")
                },
            )
            SpanText(
                text = "StandupBot",
                modifier = Modifier.classNames("sb-text-primary", "mk-nav-brand-name"),
            )
        }
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.gap(Spacing.xxs),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier
                    .classNames("sb-nav-desktop")
                    .gap(Spacing.xxs),
            ) {
                MarketingNavLink("Docs", "/docs")
                MarketingNavLink("Pricing", "/pricing")
                MarketingNavLink("Changelog", "/changelog")
                NavSignInEntry()
            }
            Box(modifier = Modifier.padding(leftRight = Spacing.xxs))
            ThemeToggle()
            Box(modifier = Modifier.padding(left = Spacing.xs))
            NavPrimaryCta()
            NavMobileDisclosure()
        }
    }
}

@Composable
private fun NavSignInEntry() {
    val auth = rememberAuthStore()
    if (auth.currentUser == null) {
        NavSignInLink()
    }
}

@Composable
private fun NavPrimaryCta() {
    val auth = rememberAuthStore()
    if (auth.currentUser != null) {
        NavCta(path = "/app", label = "Open app")
    } else {
        NavCta(path = "/oauth/slack/install", label = "Install")
    }
}

@Composable
private fun NavCta(path: String, label: String) {
    Link(
        path = path,
        modifier = Modifier.classNames("mk-btn", "mk-btn--primary", "mk-btn--sm"),
    ) {
        SpanText(label)
    }
}

@Composable
private fun NavMobileDisclosure() {
    val auth = rememberAuthStore()
    TagElement<HTMLElement>(
        tagName = "details",
        applyAttrs = Modifier
            .classNames("sb-nav-mobile")
            .styleModifier {
                property("position", "relative")
                property("margin-left", "4px")
            }
            .toAttrs(),
    ) {
        TagElement<HTMLElement>(
            tagName = "summary",
            applyAttrs = Modifier
                .classNames("sb-nav-mobile-summary", "mk-nav-menu-btn")
                .toAttrs { attr("aria-label", "Menu") },
        ) {
            SpanText("≡")
        }
        Column(modifier = Modifier.classNames("sb-nav-mobile-panel", "mk-nav-panel")) {
            MarketingNavLink("Docs", "/docs")
            MarketingNavLink("Pricing", "/pricing")
            MarketingNavLink("Changelog", "/changelog")
            if (auth.currentUser == null) {
                NavSignInLink()
            }
        }
    }
}

@Composable
private fun NavSignInLink() {
    NavLink(label = "Sign in", path = "/signin", small = true)
}

@Composable
private fun MarketingNavLink(label: String, path: String) {
    NavLink(label = label, path = path, small = false)
}

@Composable
private fun NavLink(label: String, path: String, small: Boolean) {
    val current = rememberPageContext().route.path
    val active = current == path || current.startsWith("$path/")
    Link(
        path = path,
        text = label,
        modifier = Modifier.classNames(
            "mk-nav-link",
            *listOfNotNull("mk-nav-link--small".takeIf { small }, "mk-nav-link--active".takeIf { active }).toTypedArray(),
        ),
    )
}

@Composable
fun MarketingFooter() {
    Div(attrs = { classes("mk-footer") }) {
        Div(attrs = { classes("mk-wrap") }) {
            Div(attrs = { classes("mk-footer-cols") }) {
                Reveal(index = 0, modifier = Modifier.classNames("mk-footer-brand")) {
                    Div(attrs = { classes("mk-footer-name") }) {
                        Image(src = "/logo.svg", modifier = Modifier.classNames("mk-footer-logo"))
                        SpanText(text = "StandupBot")
                    }
                    P(attrs = { classes("mk-footer-blurb") }) {
                        Text("Daily standups written by Claude from your Linear activity and posted to Slack. Made by Maytes in Sydney.")
                    }
                }
                FooterColumn(
                    index = 1,
                    heading = "Product",
                    links = listOf(
                        "How it works" to "/#pipeline",
                        "Pricing" to "/pricing",
                        "Changelog" to "/changelog",
                        "Docs" to "/docs",
                    ),
                )
                FooterColumn(
                    index = 2,
                    heading = "Company",
                    links = listOf(
                        "Contact" to "/contact",
                        "Sign in" to "/signin",
                    ),
                )
                FooterColumn(
                    index = 3,
                    heading = "Legal",
                    links = listOf(
                        "Privacy" to "/legal/privacy",
                        "Terms" to "/legal/terms",
                        "Security" to "mailto:security@standupbot.app",
                    ),
                )
            }
            Reveal {
                P(attrs = { classes("mk-footer-mark"); attr("aria-hidden", "true") }) { Text("StandupBot") }
            }
            Div(attrs = { classes("mk-footer-base") }) {
                Span { Text("© 2026 Maytes, Sydney, Australia") }
                Span { Text("Public beta") }
            }
        }
    }
}

@Composable
private fun FooterColumn(index: Int, heading: String, links: List<Pair<String, String>>) {
    Reveal(index = index, modifier = Modifier.classNames("mk-footer-col")) {
        H3(attrs = { classes("mk-footer-heading") }) { Text(heading) }
        links.forEach { (label, path) ->
            Link(
                path = path,
                text = label,
                modifier = Modifier.classNames("mk-footer-link"),
            )
        }
    }
}

@Composable
fun MarketingSection(
    bordered: Boolean = false,
    surface: Boolean = false,
    maxWidthPx: Int = 880,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier
            .then(if (surface) Modifier.classNames("sb-surface") else Modifier)
            .fillMaxWidth()
            .padding(topBottom = Spacing.xxl, leftRight = Spacing.lg)
            .styleModifier {
                if (bordered) {
                    property("border-top", "1px solid ${Palette.borderSubtle.cssRgb()}")
                }
            },
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().maxWidth(maxWidthPx.px),
        ) {
            content()
        }
    }
}

@Composable
fun PageHero(title: String, subtitle: String?) {
    PageIntro(title = title, line = subtitle.orEmpty())
}
